import { APIKit } from "config/axios/index";
import store from "config/redux/index";
import { start, end, error } from "config/redux/slice";

import { ServerConstants, ServiceConstants } from "constant";
import { ServiceModel } from "models";
import { KpiPeriod } from "types";

import { handleErrorWithTokenRenewal } from "./common";

const { API_V1_KPI_METRICS, API_V1_KPI_AGENTS } = ServerConstants;
const {
  REQUEST_KPI_METRICS_ID,
  REQUEST_KPI_DASHBOARD_ID,
  REQUEST_KPI_YEARLY_ID,
  REQUEST_KPI_TABLE_ID,
  REQUEST_KPI_SMALL_WINS_ID,
} = ServiceConstants;

export interface KpiDashboardParams {
  metric: string;
  period?: KpiPeriod;
  dateFrom?: string;
  dateTo?: string;
}

export interface KpiYearlyParams {
  metric: string;
  year: number;
}

export interface KpiTableParams {
  period?: KpiPeriod;
  dateFrom?: string;
  dateTo?: string;
}

const appendIfPresent = (params: URLSearchParams, key: string, value?: string | number) => {
  if (value !== undefined && value !== null && `${value}`.length > 0) {
    params.append(key, String(value));
  }
};

export async function kpiMetrics() {
  const requestId = REQUEST_KPI_METRICS_ID;
  try {
    store.dispatch(start(requestId));
    const response = await APIKit.get(API_V1_KPI_METRICS);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, kpiMetrics, [], requestId);
  }
}

export async function kpiDashboard(agentId: number, params: KpiDashboardParams) {
  const requestId = REQUEST_KPI_DASHBOARD_ID;
  try {
    store.dispatch(start(requestId));
    const searchParams = new URLSearchParams();
    appendIfPresent(searchParams, "metric", params.metric);
    appendIfPresent(searchParams, "period", params.period);
    appendIfPresent(searchParams, "dateFrom", params.dateFrom);
    appendIfPresent(searchParams, "dateTo", params.dateTo);
    const url = `${API_V1_KPI_AGENTS}/${agentId}/dashboard?${searchParams.toString()}`;
    const response = await APIKit.get(url);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, kpiDashboard, [agentId, params], requestId);
  }
}

export async function kpiYearly(agentId: number, params: KpiYearlyParams) {
  const requestId = REQUEST_KPI_YEARLY_ID;
  try {
    store.dispatch(start(requestId));
    const searchParams = new URLSearchParams();
    appendIfPresent(searchParams, "metric", params.metric);
    appendIfPresent(searchParams, "year", params.year);
    const url = `${API_V1_KPI_AGENTS}/${agentId}/yearly?${searchParams.toString()}`;
    const response = await APIKit.get(url);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, kpiYearly, [agentId, params], requestId);
  }
}

export async function kpiTable(agentId: number, params: KpiTableParams) {
  const requestId = REQUEST_KPI_TABLE_ID;
  try {
    store.dispatch(start(requestId));
    const searchParams = new URLSearchParams();
    appendIfPresent(searchParams, "period", params.period);
    appendIfPresent(searchParams, "dateFrom", params.dateFrom);
    appendIfPresent(searchParams, "dateTo", params.dateTo);
    const query = searchParams.toString();
    const url = query
      ? `${API_V1_KPI_AGENTS}/${agentId}/table?${query}`
      : `${API_V1_KPI_AGENTS}/${agentId}/table`;
    const response = await APIKit.get(url);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    handleErrorWithTokenRenewal(err, kpiTable, [agentId, params], requestId);
  }
}

// The table endpoint doesn't include small wins (MP), so the row is fetched from the
// per-metric dashboard endpoint under its own request id (the charts own REQUEST_KPI_DASHBOARD_ID).
// Errors are swallowed on purpose: the BE currently answers metric=smallWins with an empty 401,
// which would send handleErrorWithTokenRenewal into an endless renew/retry loop (or a logout).
// Real token expiry is still handled by the other KPI requests fired alongside this one.
export async function kpiSmallWins(agentId: number, params: KpiTableParams) {
  const requestId = REQUEST_KPI_SMALL_WINS_ID;
  try {
    store.dispatch(start(requestId));
    const searchParams = new URLSearchParams();
    appendIfPresent(searchParams, "metric", "smallWins");
    appendIfPresent(searchParams, "period", params.period);
    appendIfPresent(searchParams, "dateFrom", params.dateFrom);
    appendIfPresent(searchParams, "dateTo", params.dateTo);
    const url = `${API_V1_KPI_AGENTS}/${agentId}/dashboard?${searchParams.toString()}`;
    const response = await APIKit.get(url);
    store.dispatch(end(new ServiceModel(requestId, response.data)));
  } catch (err: any) {
    store.dispatch(error(new ServiceModel(requestId, err)));
  }
}
